import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, X, Loader2, MessageCircle, Bot, User as UserIcon } from "lucide-react";

// Iter 130.r · Rutas conocidas del SaaS para auto-enlazar respuestas del chatbot
const KNOWN_ROUTES = [
  "/correo", "/plazos-legales", "/admin/dehu-certificado", "/admin/email-inbox",
  "/notificaciones-kratos", "/admin", "/admin/legal-config",
  "/obras", "/aprobacion-facturas", "/facturacion", "/microcompras-obra",
  "/produccion-subcontratas", "/subcontratas", "/cae", "/pss", "/pss-presupuesto",
  "/licitaciones", "/analisis", "/gerencia",
  "/procedimientos", "/documental", "/bitacora", "/reuniones", "/avales",
];

/** Renderiza un string convirtiendo rutas /xxx en enlaces clickables. */
function MessageContent({ text, onLink }) {
  // Ordena por longitud descendente para que rutas largas como /admin/dehu-certificado
  // se capturen antes que /admin
  const sorted = [...KNOWN_ROUTES].sort((a, b) => b.length - a.length);
  const re = new RegExp(`(${sorted.map((r) => r.replace(/\//g, "\\/")).join("|")})`, "g");
  const parts = String(text || "").split(re);
  return (
    <>
      {parts.map((part, i) => {
        if (KNOWN_ROUTES.includes(part)) {
          return (
            <button
              key={i}
              type="button"
              onClick={() => onLink(part)}
              className="inline-block px-1.5 py-0.5 mx-0.5 rounded font-extrabold text-[12px] text-white hover:opacity-90 align-baseline"
              style={{ background: "linear-gradient(135deg, #1E5BCC, #6D43B8)" }}
            >
              {part}
            </button>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

const SUGERENCIAS = [
  "¿Cómo creo una nueva obra?",
  "¿Cómo configuro el correo IMAP?",
  "¿Cómo subo el certificado DEHú?",
  "¿Cómo apruebo facturas con 3-bandas?",
  "¿Qué documentación CAE necesito?",
  "¿Cómo importo un BC3?",
  "¿Cómo registro una microcompra?",
];

export default function AICopilotFab() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    const newMsgs = [...messages, { role: "user", content }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const { data } = await api.post("/ai-copilot/chat", { session_id: sessionId, messages: newMsgs });
      setSessionId(data.session_id);
      setMessages([...newMsgs, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages([...newMsgs, { role: "assistant", content: `⚠ ${formatErr(e)}` }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* FAB */}
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="copilot-fab"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-5 right-5 z-40 px-4 h-14 rounded-2xl flex items-center gap-2 text-white shadow-2xl font-extrabold"
        style={{
          background: "linear-gradient(135deg, #B43A4E 0%, #6D43B8 100%)",
          boxShadow: "0 14px 40px -10px #B43A4EAA, inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
        aria-label="Copiloto IA"
      >
        <span className="absolute inset-0 rounded-2xl animate-ping opacity-30" style={{ background: "linear-gradient(135deg, #B43A4E, #6D43B8)" }} />
        <span className="relative flex items-center gap-2">
          {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
          {!open && <span className="hidden sm:inline text-[11px] uppercase tracking-wider whitespace-nowrap">Pregúntame</span>}
        </span>
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[80vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl border border-[var(--legatum-border)]"
            style={{ background: "var(--legatum-surface)" }}
            data-testid="copilot-panel"
          >
            {/* Header */}
            <div className="relative px-4 py-3 text-white" style={{ background: "linear-gradient(135deg, #B43A4E 0%, #6D43B8 100%)" }}>
              <div className="absolute inset-0 opacity-[0.18] pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "12px 12px" }} />
              <div className="relative flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/40" style={{ background: "rgba(255,255,255,0.22)" }}>
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-white/85">Copiloto IA · Claude 4.5</div>
                  <div className="text-sm font-extrabold leading-tight">Tu asistente personal</div>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--legatum-bg)]">
              {messages.length === 0 ? (
                <div className="text-center py-6">
                  <Sparkles className="h-10 w-10 mx-auto text-[#B43A4E] mb-2" />
                  <div className="text-sm font-bold">Hola, soy tu copiloto IA.</div>
                  <div className="text-xs text-[var(--legatum-text-muted)] mt-1">Conozco tu rol, tus obras y tus alertas. ¿En qué te ayudo?</div>
                  <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
                    {SUGERENCIAS.map((s, i) => (
                      <button key={i} onClick={() => send(s)} data-testid={`copilot-suggest-${i}`}
                        className="text-[10px] font-bold px-2 py-1 rounded-md bg-[var(--legatum-elevated)] text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)] border border-[var(--legatum-border)] hover:border-[#B43A4E] transition-colors">
                        "{s}"
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    {m.role === "assistant" && (
                      <div className="h-7 w-7 rounded-lg shrink-0 flex items-center justify-center text-white shadow-sm" style={{ background: "linear-gradient(135deg, #B43A4E, #6D43B8)" }}>
                        <Bot className="h-3.5 w-3.5" />
                      </div>
                    )}
                    <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "rounded-tr-sm text-white font-semibold" : "rounded-tl-sm bg-[var(--legatum-elevated)] text-[var(--legatum-text)]"}`}
                         style={m.role === "user" ? { background: "linear-gradient(135deg, #1E5BCC, #1648A0)" } : {}}>
                      {m.role === "assistant"
                        ? <MessageContent text={m.content} onLink={(route) => { navigate(route); setOpen(false); }} />
                        : m.content}
                    </div>
                    {m.role === "user" && (
                      <div className="h-7 w-7 rounded-lg shrink-0 flex items-center justify-center text-white shadow-sm" style={{ background: "#1E5BCC" }}>
                        <UserIcon className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </div>
                ))
              )}
              {loading && (
                <div className="flex gap-2 justify-start">
                  <div className="h-7 w-7 rounded-lg shrink-0 flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #B43A4E, #6D43B8)" }}>
                    <Bot className="h-3.5 w-3.5" />
                  </div>
                  <div className="px-3 py-2 rounded-2xl rounded-tl-sm bg-[var(--legatum-elevated)] inline-flex items-center gap-2 text-sm text-[var(--legatum-text-muted)]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Pensando…
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[var(--legatum-border)] bg-[var(--legatum-surface)] flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Pregunta a tu copiloto…"
                disabled={loading}
                data-testid="copilot-input"
                className="flex-1 px-3 py-2 rounded-xl bg-[var(--legatum-elevated)] border border-[var(--legatum-border)] text-sm font-semibold focus:outline-none focus:border-[#B43A4E]"
              />
              <Button type="submit" disabled={loading || !input.trim()} data-testid="copilot-send"
                className="text-white border-0 shrink-0"
                style={{ background: "linear-gradient(135deg, #B43A4E, #6D43B8)" }}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
